interface GameResult {
  totalWins: number;
  totalGames: number;
  consecutiveWins: number;
  consecutiveLosses: number;
  isFirstGame: boolean;
  wonAllCoins: boolean;
  mixedResults: boolean;
}

interface TipData {
  message: string;
  tip?: string;
}

export const generateTip = (gameResult: GameResult): TipData | null => {
  const {
    totalWins,
    totalGames,
    consecutiveWins,
    consecutiveLosses,
    isFirstGame,
    wonAllCoins,
    mixedResults,
  } = gameResult;

  // First game win
  if (isFirstGame && totalWins > 0) {
    const firstGameMessages = [
      "First flip. First win. First of many.",
      "Beginner's luck? Or natural-born strategist?",
      "You got it right on the first try — let's see how deep the rabbit hole goes.",
      "This is how legends start. Welcome to Fourhead.",
    ];
    return {
      message:
        firstGameMessages[Math.floor(Math.random() * firstGameMessages.length)],
    };
  }

  // Won all coins (Four of a Coin'd)
  if (wonAllCoins) {
    const fourWinMessages = [
      {
        message:
          "Four heads. Four wins. You're clearly not just flipping — you're calculating.",
        tip: "Watch the odds. The 60/40 coin is where risk meets reward.",
      },
      {
        message:
          "Four wins in a row? You're reading the game like a psychic. Keep your streak alive.",
        tip: "Stack your bets — sometimes spreading across coins wins more than going all in.",
      },
    ];
    return fourWinMessages[Math.floor(Math.random() * fourWinMessages.length)];
  }

  // Four consecutive wins + strategy tip
  if (consecutiveWins >= 4) {
    const streakMessages = [
      {
        message:
          "Back-to-back plays like that don't just happen. You're locked in.",
        tip: "Switch up your rhythm. Mixing coins with different odds keeps you ahead.",
      },
      {
        message: "You've found the signal in the chaos.",
        tip: "Don't ignore the 50/50 coin. When in doubt, go balanced.",
      },
    ];
    return streakMessages[Math.floor(Math.random() * streakMessages.length)];
  }

  // Mixed results (winning some, losing some)
  if (mixedResults && totalGames > 2) {
    const mixedMessages = [
      "It's a dance — a little loss, a little win. But you're still on beat.",
      "Every flip teaches. The next one rewards.",
      "Mixed bag? Perfect. That's where the real patterns show.",
      "Even the best miss shots — but they never stop shooting.",
      "Your gut was half-right. Now sharpen it.",
    ];
    return {
      message: mixedMessages[Math.floor(Math.random() * mixedMessages.length)],
    };
  }

  // Tips after losing
  if (consecutiveLosses >= 2 || (totalGames > 0 && totalWins === 0)) {
    const losingTips = [
      {
        message:
          "Every coin has its rhythm. Switch coins — don't chase the last one.",
        tip: "Zoom out. The 70/30 coin pays safer — collect, then strike bold.",
      },
      {
        message: "Losing means learning — don't tilt, reset. Flip smart.",
        tip: "Try pairing coins. Win on one, hedge on the other. It's strategy, not luck.",
      },
      {
        message:
          "Use the odds. The higher the tail chance, the sweeter the bounce-back.",
        tip: "Every coin has its rhythm. Switch coins — don't chase the last one.",
      },
    ];
    return losingTips[Math.floor(Math.random() * losingTips.length)];
  }

  return null;
};
